"use client";

import { useEffect, useState } from "react";

// Hot Sale ends June 2, 2026 at 23:59:59 CST (UTC-6)
export const HOT_SALE_END = new Date("2026-06-03T05:59:59Z").getTime();
export const HOT_SALE_START = new Date("2026-05-25T06:00:00Z").getTime();

export function isHotSaleActive() {
  const now = Date.now();
  return now >= HOT_SALE_START && now < HOT_SALE_END;
}

interface CountdownTimerProps {
  className?: string;
  /** Compact mode for the promo bar (single line) */
  compact?: boolean;
}

function getTimeLeft() {
  const diff = HOT_SALE_END - Date.now();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

export default function CountdownTimer({ className = "", compact = false }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<ReturnType<typeof getTimeLeft>>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setTimeLeft(getTimeLeft());
    const interval = setInterval(() => {
      const tl = getTimeLeft();
      setTimeLeft(tl);
      if (!tl) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // SSR: render nothing until hydrated to avoid mismatch
  if (!mounted || !timeLeft) return null;

  const pad = (n: number) => String(n).padStart(2, "0");

  if (compact) {
    return (
      <span className={className}>
        {timeLeft.days}d {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m {pad(timeLeft.seconds)}s
      </span>
    );
  }

  return (
    <div className={`flex items-center gap-3 md:gap-4 ${className}`}>
      {[
        { value: timeLeft.days, label: "días" },
        { value: timeLeft.hours, label: "hrs" },
        { value: timeLeft.minutes, label: "min" },
        { value: timeLeft.seconds, label: "seg" },
      ].map((unit) => (
        <div key={unit.label} className="text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 md:px-4 md:py-3 min-w-[56px] md:min-w-[68px] border border-white/20">
            <span className="text-2xl md:text-4xl font-bold font-mono tabular-nums">
              {pad(unit.value)}
            </span>
          </div>
          <span className="text-[10px] md:text-xs font-mono uppercase tracking-wider opacity-80 mt-1 block">
            {unit.label}
          </span>
        </div>
      ))}
    </div>
  );
}
